import { motion } from 'framer-motion';
import { ArrowRight, Shield, FileCheck, Scale } from 'lucide-react';
import { Button } from './ui/button';

export const HeroSection = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #fdfdfc 0%, rgba(10,25,47,0.06) 40%, rgba(217,48,37,0.08) 100%)'
      }}
      data-testid="hero-section"
    >
      {/* Background Gradient Orbs */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-[#0a192f]/8 rounded-full blur-3xl animate-float" />
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-[#D93025]/10 rounded-full blur-3xl animate-float-delayed" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-[#0a192f]/5 to-[#D93025]/5 rounded-full blur-3xl" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32 md:py-40">
        {/* Centered Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center max-w-4xl mx-auto space-y-8"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#f5f5f0] rounded-full border border-[#e2e2dc]"
          >
            <span className="w-2 h-2 bg-[#D93025] rounded-full animate-pulse" />
            <span className="text-xs font-bold uppercase tracking-widest text-[#64748b]">
              Trusted Legal Support
            </span>
          </motion.div>

          {/* Headline */}
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0a192f] leading-[1.1]">
            Professional Legal Support for{' '}
            <span className="relative inline-block">
              Modern Law Firms
              <svg
                className="absolute -bottom-2 left-0 w-full"
                height="8"
                viewBox="0 0 200 8"
                fill="none"
              >
                <path
                  d="M2 6C50 2 150 2 198 6"
                  stroke="#D93025"
                  strokeWidth="3"
                  strokeLinecap="round"
                  opacity="0.6"
                />
              </svg>
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-base md:text-lg leading-relaxed text-[#64748b] max-w-2xl mx-auto">
            Streamline your legal operations with precision, compliance, and efficiency. 
            We deliver detail-oriented legal support services that help your firm focus on 
            what matters most — winning cases.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button
              onClick={() => scrollToSection('#contact')}
              className="bg-[#0a192f] text-white rounded-xl px-8 py-4 font-medium hover:bg-[#0a192f]/90 transition-all hover:scale-[1.02] shadow-lg shadow-[#0a192f]/20 text-base"
              data-testid="hero-cta-consultation"
            >
              Schedule Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              onClick={() => scrollToSection('#services')}
              variant="outline"
              className="bg-[#f5f5f0] text-[#0a192f] rounded-xl px-8 py-4 font-medium hover:bg-[#f5f5f0]/80 transition-all border border-[#e2e2dc] text-base"
              data-testid="hero-cta-services"
            >
              View Services
            </Button>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap gap-6 justify-center pt-6">
            <div className="flex items-center gap-2 text-sm text-[#64748b]">
              <Shield className="h-4 w-4 text-[#0a192f]" />
              <span>Confidential</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#64748b]">
              <FileCheck className="h-4 w-4 text-[#0a192f]" />
              <span>Secure</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#64748b]">
              <Scale className="h-4 w-4 text-[#0a192f]" />
              <span>Reliable</span>
            </div>
          </div>

          {/* Stats Row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="flex flex-wrap justify-center gap-8 md:gap-16 pt-8"
          >
            <div className="text-center">
              <p className="font-heading text-3xl md:text-4xl font-semibold text-[#0a192f]">500+</p>
              <p className="text-sm text-[#64748b]">Cases Supported</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-3xl md:text-4xl font-semibold text-[#0a192f]">15+</p>
              <p className="text-sm text-[#64748b]">Years Experience</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-3xl md:text-4xl font-semibold text-[#0a192f]">98%</p>
              <p className="text-sm text-[#64748b]">Client Satisfaction</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
